/**
 * 标签管理服务
 * Tag Management Service
 */

import apiClient from './api';
import type {
  ApiResponse,
  TagDefinition,
  TagDefinitionCreate,
  TagDefinitionUpdate,
  TagListResponse,
  TagsByCategoryResponse,
  TagOption,
  TagCategoryInfo,
  TagQueryParams,
  TagBatchCreateRequest,
  TagBatchCreateResponse,
} from '../types/tag';

// API 基础路径
const BASE_URL = '/admin/tags';

// ============================================================
// 标签定义管理 API
// ============================================================

/**
 * 获取标签定义列表
 * 注意：API 拦截器会解包 response.data.data，所以直接返回 data 部分
 */
export async function getTagList(params: TagQueryParams = {}): Promise<TagListResponse> {
  const response = await apiClient.get<ApiResponse<any>>(
    `${BASE_URL}/definitions`,
    { params }
  );
  // 拦截器已将 response.data.data 解包为 response.data
  return response.data;
}

/**
 * 获取标签详情
 * 注意：API 拦截器会解包 response.data.data
 */
export async function getTagDefinition(tagId: number): Promise<TagDefinition | null> {
  try {
    const response = await apiClient.get<ApiResponse<any>>(
      `${BASE_URL}/definitions/${tagId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get tag definition:', error);
    return null;
  }
}

/**
 * 创建标签定义
 * 注意：API 拦截器会解包 response.data.data
 */
export async function createTagDefinition(tagData: TagDefinitionCreate): Promise<TagDefinition> {
  const response = await apiClient.post<ApiResponse<any>>(
    `${BASE_URL}/definitions`,
    tagData
  );
  return response.data;
}

/**
 * 获取或创建标签（原子操作）
 * 用于用户编辑页面，输入新标签时自动创建
 * 注意：API 拦截器会解包 response.data.data
 */
export async function getOrCreateTag(
  tagName: string,
  category: string,
  displayName?: string
): Promise<{ tag: TagDefinition; is_created: boolean }> {
  const response = await apiClient.post<ApiResponse<any>>(
    `${BASE_URL}/definitions/get-or-create`,
    null,
    {
      params: {
        tag_name: tagName,
        tag_category: category,
        display_name: displayName,
      },
    }
  );
  return response.data;
}

/**
 * 更新标签定义
 * 注意：API 拦截器会解包 response.data.data
 */
export async function updateTagDefinition(
  tagId: number,
  tagData: TagDefinitionUpdate
): Promise<TagDefinition> {
  const response = await apiClient.put<ApiResponse<any>>(
    `${BASE_URL}/definitions/${tagId}`,
    tagData
  );
  return response.data;
}

/**
 * 删除标签定义
 */
export async function deleteTagDefinition(tagId: number, hardDelete = false): Promise<void> {
  await apiClient.delete(`${BASE_URL}/definitions/${tagId}`, {
    params: { hard_delete: hardDelete },
  });
}

// ============================================================
// 按分类查询 API（前端下拉框使用）
// ============================================================

/**
 * 按分类查询标签选项（用于 Select 下拉框）
 * 注意：API 拦截器会解包 response.data.data，所以直接返回 data 部分
 */
export async function getTagsByCategory(
  category: string,
  isActive = true
): Promise<TagsByCategoryResponse> {
  const response = await apiClient.get<ApiResponse<any>>(
    `${BASE_URL}/definitions/by-category/${category}`,
    { params: { is_active: isActive } }
  );
  // 拦截器已将 response.data.data 解包为 response.data
  // 直接返回 data 部分
  return response.data;
}

/**
 * 获取性格标签选项
 */
export async function getPersonalityTagOptions(): Promise<TagOption[]> {
  const result = await getTagsByCategory('personality');
  return result?.tags || [];
}

/**
 * 获取兴趣标签选项
 */
export async function getInterestTagOptions(): Promise<TagOption[]> {
  const result = await getTagsByCategory('interest');
  return result?.tags || [];
}

// ============================================================
// 批量操作 API
// ============================================================

/**
 * 批量创建标签
 * 注意：API 拦截器会解包 response.data.data
 */
export async function batchCreateTags(
  request: TagBatchCreateRequest
): Promise<TagBatchCreateResponse> {
  const response = await apiClient.post<ApiResponse<any>>(
    `${BASE_URL}/definitions/batch`,
    request
  );
  return response.data;
}

// ============================================================
// 统计 API
// ============================================================

/**
 * 获取标签分类统计
 * 注意：API 拦截器会解包 response.data.data
 */
export async function getCategoryStats(): Promise<TagCategoryInfo[]> {
  const response = await apiClient.get<ApiResponse<any>>(
    `${BASE_URL}/categories`
  );
  return response.data;
}

// ============================================================
// 导出所有
// ============================================================

const tagService = {
  // 列表
  getTagList,
  getTagDefinition,
  createTagDefinition,
  getOrCreateTag,
  updateTagDefinition,
  deleteTagDefinition,

  // 按分类查询
  getTagsByCategory,
  getPersonalityTagOptions,
  getInterestTagOptions,

  // 批量操作
  batchCreateTags,

  // 统计
  getCategoryStats,
};

export default tagService;
